import React, { useState, useEffect } from 'react';
import apiService from '../../services/ApiService';
import { usePortfolioContext } from './PortfolioContext';

export default function SharingManager() {
    const { selectedGroup, selectGroup, canShare } = usePortfolioContext();
    const [sharingEntries, setSharingEntries] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [showInvite, setShowInvite] = useState(false);

    useEffect(() => {
        if (!selectedGroup) return;
        fetchSharing();
    }, [selectedGroup?.id]);

    const fetchSharing = async () => {
        if (!selectedGroup) return;
        setLoading(true);
        try {
            const response = await apiService.getPortfolioGroupSharing(selectedGroup.id);
            if (response && response.ok) {
                setSharingEntries(await response.json());
            }
        } finally {
            setLoading(false);
        }
    };

    const handleRevoke = async (sharingId: number) => {
        if (!selectedGroup) return;
        await apiService.deletePortfolioGroupSharing(selectedGroup.id, sharingId);
        await fetchSharing();
    };

    if (!selectedGroup) return null;

    return (
        <div className="space-y-6">
            {/* Sharing list */}
            <section className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-base font-semibold text-slate-900">
                        Shared with ({sharingEntries.length})
                    </h3>
                    {canShare && (
                        <button
                            onClick={() => setShowInvite(true)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                        >
                            <span className="material-symbols-outlined text-[16px]">person_add</span>
                            Invite
                        </button>
                    )}
                </div>

                {loading ? (
                    <div className="flex justify-center py-8">
                        <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                    </div>
                ) : sharingEntries.length === 0 ? (
                    <div className="text-center py-8 text-slate-400">
                        <span className="material-symbols-outlined text-[40px] block mb-2">group_off</span>
                        <p className="text-sm">This portfolio is not shared with anyone.</p>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {sharingEntries.map((entry: any) => (
                            <div key={entry.id} className="flex items-center justify-between py-3 px-4 rounded-lg bg-slate-50">
                                <div>
                                    <div className="text-sm font-medium text-slate-900">{entry.email}</div>
                                    <div className="flex gap-2 mt-1">
                                        {entry.right_to_read && (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">Read</span>
                                        )}
                                        {entry.right_to_write && (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">Write</span>
                                        )}
                                        {entry.right_to_share && (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">Share</span>
                                        )}
                                    </div>
                                </div>
                                {canShare && (
                                    <button
                                        onClick={() => handleRevoke(entry.id)}
                                        className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                                        title="Revoke access"
                                    >
                                        <span className="material-symbols-outlined text-[18px]">person_remove</span>
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* Invite modal */}
            {showInvite && (
                <InviteForm
                    groupId={selectedGroup.id}
                    onClose={() => setShowInvite(false)}
                    onInvited={() => { setShowInvite(false); fetchSharing(); }}
                />
            )}
        </div>
    );
}

interface OrgUser {
    id: number;
    email: string;
    first_name: string | null;
    last_name: string | null;
}

function InviteForm({ groupId, onClose, onInvited }: { groupId: number; onClose: () => void; onInvited: () => void }) {
    const [users, setUsers] = useState<OrgUser[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [rightToRead, setRightToRead] = useState(true);
    const [rightToWrite, setRightToWrite] = useState(false);
    const [rightToShare, setRightToShare] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const response = await apiService.getPortfolioGroupAvailableUsers(groupId);
                if (!cancelled && response && response.ok) {
                    setUsers(await response.json());
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [groupId]);

    const filteredUsers = users.filter(u => {
        if (!search.trim()) return true;
        const q = search.toLowerCase();
        const fullName = `${u.first_name || ''} ${u.last_name || ''}`.toLowerCase();
        return fullName.includes(q) || u.email.toLowerCase().includes(q);
    });

    const toggleUser = (id: number) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (selectedIds.size === 0) return;
        if (!rightToRead && !rightToWrite && !rightToShare) {
            setError('Select at least one permission.');
            return;
        }

        setSaving(true);
        setError('');
        try {
            const response = await apiService.sharePortfolioGroup(groupId, {
                users: Array.from(selectedIds).map(user_id => ({
                    user_id,
                    right_to_read: rightToRead,
                    right_to_write: rightToWrite,
                    right_to_share: rightToShare,
                })),
            });
            if (response && response.ok) {
                onInvited();
            } else {
                setError('Failed to share.');
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="p-6 flex flex-col gap-4 overflow-hidden">
                    <h3 className="text-lg font-semibold text-slate-900">Share with organization members</h3>

                    <form onSubmit={handleSubmit} className="space-y-4 flex flex-col flex-1 overflow-hidden">
                        <div className="flex flex-col flex-1 overflow-hidden">
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                                Members ({selectedIds.size} selected)
                            </label>
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search by name or email..."
                                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 mb-2"
                                autoFocus
                            />
                            <div className="border border-slate-200 rounded-lg overflow-auto" style={{ maxHeight: 240 }}>
                                {loading ? (
                                    <div className="flex justify-center py-6">
                                        <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                                    </div>
                                ) : filteredUsers.length === 0 ? (
                                    <p className="text-center py-6 text-sm text-slate-400">
                                        {users.length === 0 ? 'No teammates available to share with.' : 'No match.'}
                                    </p>
                                ) : (
                                    filteredUsers.map(user => {
                                        const checked = selectedIds.has(user.id);
                                        return (
                                            <label
                                                key={user.id}
                                                className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-b-0"
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    onChange={() => toggleUser(user.id)}
                                                    className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                                />
                                                <div className="min-w-0 flex-1">
                                                    <div className="text-sm font-medium text-slate-900 truncate">
                                                        {(user.first_name || user.last_name) ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : user.email}
                                                    </div>
                                                    {(user.first_name || user.last_name) && (
                                                        <div className="text-xs text-slate-500 truncate">{user.email}</div>
                                                    )}
                                                </div>
                                            </label>
                                        );
                                    })
                                )}
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">Permissions</label>
                            <div className="space-y-2">
                                <label className={`flex items-center gap-2 ${(rightToWrite || rightToShare) ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                                    <input
                                        type="checkbox"
                                        checked={rightToRead}
                                        disabled={rightToWrite || rightToShare}
                                        onChange={(e) => setRightToRead(e.target.checked)}
                                        className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                                    />
                                    <span className="text-sm text-slate-700">Read</span>
                                    <span className="text-xs text-slate-400">— View portfolio and computed results</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={rightToWrite}
                                        onChange={(e) => {
                                            setRightToWrite(e.target.checked);
                                            if (e.target.checked) setRightToRead(true);
                                        }}
                                        className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    <span className="text-sm text-slate-700">Write</span>
                                    <span className="text-xs text-slate-400">— Add/remove assets, edit group settings, trigger compute</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={rightToShare}
                                        onChange={(e) => {
                                            setRightToShare(e.target.checked);
                                            if (e.target.checked) setRightToRead(true);
                                        }}
                                        className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    <span className="text-sm text-slate-700">Share</span>
                                    <span className="text-xs text-slate-400">— Invite other users to this portfolio</span>
                                </label>
                            </div>
                        </div>

                        {error && <p className="text-xs text-red-500">{error}</p>}

                        <div className="flex justify-end gap-3 pt-2">
                            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg transition-colors">
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={selectedIds.size === 0 || saving}
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
                            >
                                {saving ? 'Sharing...' : `Share${selectedIds.size > 1 ? ` (${selectedIds.size})` : ''}`}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
